import { Workflow } from './types';
import moment from 'moment';
import 'moment-timezone';
import { ZERO_CONTACT_ARCHIVE_THRESHOLD } from '../../../functions/src/workflows/constants';

function nextRunDateTime(cronString: string) {
  const [minute, hour, dayOfMonth, month, dayOfWeek] = cronString.split(' ');
  const now = moment().tz('America/Los_Angeles');
  const nextRun = moment(now);

  // Helper function to check if a value matches the cron field
  function matches(value, cronField) {
    if (cronField === '*') return true;
    if (cronField.includes(','))
      return cronField.split(',').includes(value.toString());
    if (cronField.includes('-')) {
      const [start, end] = cronField.split('-');
      return value >= parseInt(start) && value <= parseInt(end);
    }
    if (cronField.includes('/')) {
      const [, step] = cronField.split('/');
      return value % parseInt(step) === 0;
    }
    return value.toString() === cronField;
  }

  // Find the next matching time
  while (true) {
    if (
      matches(nextRun.minutes(), minute) &&
      matches(nextRun.hours(), hour) &&
      matches(nextRun.date(), dayOfMonth) &&
      matches(nextRun.month() + 1, month) &&
      matches(nextRun.day(), dayOfWeek)
    ) {
      return nextRun.local();
    }
    nextRun.add(1, 'minute');
    nextRun.seconds(0);
    nextRun.milliseconds(0);
  }
}

/** Human-readable Daily Schedule column — reflects run state, not legacy lifecycle stages. */
export function getReadableWorkflowLifecycleStage(workflow: Workflow) {
  const nextRun = nextRunDateTime(workflow.schedule || '0 0 * * *');
  const streak = workflow.zero_contact_streak ?? 0;

  // Mid-run: RTDB shows running; don't imply the workflow is idle until tomorrow
  if (workflow.last_execution_result === 'running') {
    return 'daily; migration in progress';
  }

  if (streak > 0) {
    // 30 consecutive zero-contact days triggers backend auto-archive (see archiveEligibility)
    return `daily; no contacts (${streak}/${ZERO_CONTACT_ARCHIVE_THRESHOLD} days); next run: ${nextRun.format('ddd, DD')}`;
  }

  // Errored but scheduler still enabled — next cron fire is scheduled; error is from last completed run
  if (
    workflow.last_execution_result === 'error' &&
    workflow.status === 'enabled'
  ) {
    return `daily; last run failed; next run: ${nextRun.format('ddd, DD')}`;
  }

  // Default daily schedule copy; paused/disabled workflows show scheduler status instead of next run
  let subtitle = `next run: ${nextRun.format('ddd, DD')}`;
  if (workflow.status !== 'enabled') subtitle = `workflow ${workflow.status}`;
  return `daily; ${subtitle}`;
}

export type WorkflowSearchRow = {
  description?: string;
  args?: {
    source_account?: number;
    source_addressbook?: number;
    destination_account?: number;
    destination_addressbook?: number;
    form_id?: string;
  };
};

/** Search blob for Description column — description text plus bare IDs from args. */
export function getDescriptionSearchValue(row: WorkflowSearchRow): string {
  const parts = [row.description || ''];
  const { args } = row;

  if (args) {
    if (args.source_account != null) {
      parts.push(String(args.source_account));
    }
    if (args.source_addressbook != null) {
      parts.push(String(args.source_addressbook));
    }
    if (args.destination_account != null) {
      parts.push(String(args.destination_account));
    }
    if (args.destination_addressbook != null) {
      parts.push(String(args.destination_addressbook));
    }
    if (args.form_id) {
      parts.push(args.form_id);
    }
  }

  return parts.join('\n');
}

const normalizeDescriptionQuery = (filterValue: string) =>
  String(filterValue).trim().toLowerCase().replace(/[()]/g, '');

/** Match pasted account/addressbook IDs — with or without parentheses. */
export function workflowMatchesDescriptionFilter(
  row: WorkflowSearchRow,
  filterValue: string
): boolean {
  const query = normalizeDescriptionQuery(filterValue);
  if (!query) {
    return true;
  }

  const searchBlob = getDescriptionSearchValue(row).toLowerCase();
  const blobWithoutParens = searchBlob.replace(/[()]/g, '');

  if (
    searchBlob.includes(query) ||
    blobWithoutParens.includes(query)
  ) {
    return true;
  }

  const args = row.args;
  if (!args) {
    return false;
  }

  const ids = [
    args.source_account,
    args.source_addressbook,
    args.destination_account,
    args.destination_addressbook,
    args.form_id,
  ]
    .filter((value) => value != null && `${value}` !== '')
    .map((value) => String(value).toLowerCase());

  if (/^\d+$/.test(query)) {
    return ids.some((id) => id === query);
  }

  return ids.some((id) => id.includes(query));
}

export const descriptionColumnFilterFn = (rows, _id, filterValue) => {
  if (!filterValue) {
    return rows;
  }

  return rows.filter((row) =>
    workflowMatchesDescriptionFilter(row.original, filterValue)
  );
};

descriptionColumnFilterFn.autoRemove = (val) => !val;
